import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { Box, IconButton, TextField, Typography } from 'seagull';
import { ColorThumbDown, ColorThumbUp, ReturnArrow, ThumbDown, ThumbUp } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import styles from './AnalystFeedbackBar.module.scss';
export const MAX_FEEDBACK_COMMENT_LENGTH = 500;
const DEFAULT_DISMISS_DELAY_MS = 1800;
const LEAVE_MS = 250;
export const GetAnalystFeedbackBarTID = (parentTestId)=>getTestIds(parentTestId);
export const AnalystFeedbackBar = (param)=>{
    let { onSubmit, onDismiss, dismissDelayMs = DEFAULT_DISMISS_DELAY_MS, testId } = param;
    const { t } = useLocaleKeys('analystAgent');
    const { AnalystFeedbackBarTID } = useMemo(()=>GetAnalystFeedbackBarTID(testId), [
        testId
    ]);
    const [rating, setRating] = useState(null);
    const [comment, setComment] = useState('');
    const [status, setStatus] = useState('idle');
    const [leaving, setLeaving] = useState(false);
    useEffect(()=>{
        if (status !== 'submitted') return undefined;
        const fadeTimer = setTimeout(()=>setLeaving(true), Math.max(0, dismissDelayMs - LEAVE_MS));
        const dismissTimer = setTimeout(()=>onDismiss === null || onDismiss === void 0 ? void 0 : onDismiss(), dismissDelayMs);
        return ()=>{
            clearTimeout(fadeTimer);
            clearTimeout(dismissTimer);
        };
    // WHY: capture onDismiss as of submission; depending on its identity would restart the dismiss timer on every parent re-render.
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
    }, [
        status,
        dismissDelayMs
    ]);
    if (status === 'submitted') {
        return /*#__PURE__*/ _jsx(Box, {
            className: cn(styles.root, styles.confirmation, {
                [styles.leaving]: leaving
            }),
            testId: AnalystFeedbackBarTID.Root(),
            role: "status",
            "aria-live": "polite",
            children: /*#__PURE__*/ _jsx(Typography, {
                className: styles.success,
                skin: "secondary",
                testId: AnalystFeedbackBarTID.Success(),
                children: t.feedback.success()
            })
        });
    }
    const chooseRating = (next)=>{
        setRating(next);
        setStatus('idle');
        // WHY: record the rating immediately (overridable by switching); surface an error only while still idle so a concurrent finalize/dismiss isn't clobbered.
        void onSubmit(next, undefined).catch(()=>setStatus((current)=>current === 'idle' ? 'error' : current));
    };
    const submit = async ()=>{
        if (rating == null || status === 'finalizing') return;
        setStatus('finalizing');
        const trimmed = comment.trim();
        try {
            await onSubmit(rating, trimmed.length > 0 ? trimmed : undefined);
            setStatus('submitted');
        } catch (unused) {
            setStatus('error');
        }
    };
    const ratingChosen = rating != null;
    const finalizing = status === 'finalizing';
    const handleCommentKeyDown = (event)=>{
        if (event.nativeEvent.isComposing) return;
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            void submit();
        }
    };
    return /*#__PURE__*/ _jsxs(Box, {
        className: styles.root,
        testId: AnalystFeedbackBarTID.Root(),
        children: [
            /*#__PURE__*/ _jsxs(Box, {
                className: styles.rateRow,
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        skin: "secondary",
                        testId: AnalystFeedbackBarTID.Prompt(),
                        children: t.feedback.prompt()
                    }),
                    /*#__PURE__*/ _jsx(IconButton, {
                        bi: {
                            name: 'analyst_feedback_thumbs_up'
                        },
                        skin: "transparent",
                        noPadding: true,
                        size: "small",
                        className: cn(styles.thumb, {
                            [styles.dimmed]: ratingChosen && rating !== 'thumbsUp'
                        }),
                        testId: AnalystFeedbackBarTID.ThumbsUp(),
                        onClick: ()=>chooseRating('thumbsUp'),
                        disabled: finalizing,
                        "aria-pressed": rating === 'thumbsUp',
                        "aria-label": t.feedback.thumbsUp(),
                        children: rating === 'thumbsUp' ? /*#__PURE__*/ _jsx(ColorThumbUp, {
                            size: 20,
                            testId: AnalystFeedbackBarTID.ThumbsUpFilled()
                        }) : /*#__PURE__*/ _jsx(ThumbUp, {
                            size: 20
                        })
                    }),
                    /*#__PURE__*/ _jsx(IconButton, {
                        bi: {
                            name: 'analyst_feedback_thumbs_down'
                        },
                        skin: "transparent",
                        noPadding: true,
                        size: "small",
                        className: cn(styles.thumb, {
                            [styles.dimmed]: ratingChosen && rating !== 'thumbsDown'
                        }),
                        testId: AnalystFeedbackBarTID.ThumbsDown(),
                        onClick: ()=>chooseRating('thumbsDown'),
                        disabled: finalizing,
                        "aria-pressed": rating === 'thumbsDown',
                        "aria-label": t.feedback.thumbsDown(),
                        children: rating === 'thumbsDown' ? /*#__PURE__*/ _jsx(ColorThumbDown, {
                            size: 20,
                            testId: AnalystFeedbackBarTID.ThumbsDownFilled()
                        }) : /*#__PURE__*/ _jsx(ThumbDown, {
                            size: 20
                        })
                    })
                ]
            }),
            ratingChosen && /*#__PURE__*/ _jsxs(Box, {
                className: styles.revealed,
                children: [
                    /*#__PURE__*/ _jsx(TextField, {
                        bi: {
                            name: 'analyst_feedback_comment'
                        },
                        size: "small",
                        multiline: true,
                        width: "100%",
                        value: comment,
                        onChange: (event)=>setComment(event.target.value),
                        inputProps: {
                            maxLength: MAX_FEEDBACK_COMMENT_LENGTH,
                            onKeyDown: handleCommentKeyDown
                        },
                        placeholder: t.feedback.commentPlaceholder(),
                        testId: AnalystFeedbackBarTID.Comment(),
                        suffix: /*#__PURE__*/ _jsx(IconButton, {
                            bi: {
                                name: 'analyst_feedback_submit'
                            },
                            className: styles.submitButton,
                            skin: "transparent",
                            size: "small",
                            noPadding: true,
                            disabled: finalizing,
                            testId: AnalystFeedbackBarTID.Submit(),
                            onClick: ()=>void submit(),
                            "aria-label": t.feedback.submit(),
                            children: /*#__PURE__*/ _jsx(ReturnArrow, {
                                size: 24
                            })
                        })
                    }),
                    status === 'error' && /*#__PURE__*/ _jsx(Typography, {
                        skin: "error",
                        testId: AnalystFeedbackBarTID.Error(),
                        children: t.transport.message()
                    })
                ]
            })
        ]
    });
};
